<template>
  <SettingsPaneV2 header="Personal profile">
    <template #subheader>
      This profile is your global profile, that is used in any team you are part of.
    </template>
    <template #body>
      <div class="team__body__section">
        <div class="subheader__title">Details</div>
        <div class="subheader__text">Update your information and manage your account settings.</div>
      </div>

      <div class="team__body__section">
        <AvatarUpload
          v-if="membershipId"
          :src="membershipStore.memberAvatarUrlById[membershipId]"
          :name="membershipStore.memberNameById[membershipId]"
          can-upload
          no-reset
          :disabled="busy"
          :loading="!!avatarData && busy"
          upload-item-text="Upload profile picture"
          @change="onAvatarChange"
        />

        <div class="row">
          <InputField
            v-model="firstName"
            :disabled="busy"
            :error="error.firstName"
            name="firstName"
            label="First Name"
          />
          <InputField
            v-model="lastName"
            :disabled="busy"
            :error="error.lastName"
            name="lastName"
            label="Last Name"
          />
        </div>

        <div class="row">
          <InputField
            v-if="userStore.currentUser"
            v-model="userStore.currentUser.email"
            disabled
            name="email"
            label="Email"
          />
        </div>

        <div class="team__pair">
          <div class="team__key">
            Account created on
            <span class="team__value">
              {{ createdAt }}
            </span>
          </div>
        </div>
      </div>

      <Divider class="divider" />

      <div class="team__body__section">
        <div class="control__toggle">
          <div class="control__toggle-description">
            <p class="control__toggle-label">Password</p>
            <p class="control__toggle-sub-label">Edit your password</p>
          </div>
          <DarwinButton
            size="sm"
            kind="secondary"
            flair="rounded"
            :disabled="busy"
            @click="changePasswordDialogOpen = true"
          >
            Edit password
          </DarwinButton>
        </div>
      </div>

      <div class="team__body__section">
        <div class="control__toggle">
          <Toggle
            v-model="enableTwoFactorAuthentication"
            kind="primary"
            size="small"
            :disabled="twoFactorEnforced"
          />
          <div class="control__toggle-description">
            <p class="control__toggle-label">Two-factor authentication (2FA)</p>
            <p class="control__toggle-sub-label">
              Add an extra layer of security to your account by requiring a verification code when
              you log in.
            </p>
          </div>
          <DarwinButton
            size="sm"
            kind="secondary"
            flair="rounded"
            :disabled="!enableTwoFactorAuthentication && !twoFactorEnforced"
            @click="configure2fa"
          >
            Configure 2FA
          </DarwinButton>
        </div>
      </div>

      <Divider class="divider" />

      <div class="team__body__section">
        <div class="control__toggle">
          <Toggle
            v-model="showNotifications"
            kind="primary"
            size="small"
            :disabled="busy"
          />
          <div class="control__toggle-description">
            <p class="control__toggle-label">Receive email notifications</p>
            <p class="control__toggle-sub-label">
              If you opt-out, you will only receive in-app notifications
            </p>
          </div>
        </div>
      </div>

      <Divider class="divider" />

      <div class="team__delete">
        <div class="team__delete--text">
          <div class="subheader__title">Delete account</div>
          <div class="subheader__text">
            This is irreversible and will delete your personal account
          </div>
        </div>
        <DarwinButton
          size="sm"
          kind="negative"
          flair="rounded"
          :disabled="busy"
          @click="onDeleteAccount"
        >
          Delete My Account
        </DarwinButton>
      </div>
    </template>

    <template #footer>
      <DarwinButton
        size="md"
        kind="primary"
        flair="rounded"
        :disabled="busy"
        @click="maybeSubmit"
      >
        Save
      </DarwinButton>
    </template>

    <DarwinSafeDialog
      :open="confirmDeleteAccountDialoOpen"
      :target-text="`DELETE ${membershipId ? membershipStore.memberNameById[membershipId] : ''}`"
      :min-height="260"
      :max-width="600"
      kind="negative"
      @cancel="confirmDeleteAccountDialoOpen = false"
      @confirm="deleteAccount"
    >
      <template #title>Please confirm you wish to delete your account</template>
      <template #body>
        Please note that deleting your account is irreversible. To confirm that you wish to proceed
        deleting this account, please type the following: 'DELETE
        {{ membershipId ? membershipStore.memberNameById[membershipId] : '' }}'
      </template>
      <template #confirm-text> Delete </template>
    </DarwinSafeDialog>

    <PasswordChangeModal
      :open="changePasswordDialogOpen"
      @close="changePasswordDialogOpen = false"
      @confirmed="changePasswordDialogOpen = false"
    />

    <SetupTfaModal
      :open="setupTfaDialogOpen"
      :setup-required="!userStore.currentUser?.two_factor_auth_enabled"
      :enable-tfa="enableTwoFactorAuthentication"
      @close="setupTfaDialogOpen = false"
      @confirmed="onTfaConfirmed"
    />
  </SettingsPaneV2>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, watch } from 'vue'

import { useUserStore } from '@/modules/Auth/useUserStore'
import { deleteProfile } from '@/backend/darwin/deleteProfile'
import { updateProfile } from '@/backend/darwin/updateProfile'
import { uploadProfileAvatar } from '@/backend/darwin/uploadProfileAvatar'

import AvatarUpload from '@/components/Common/AvatarUpload/AvatarUpload.vue'
import type { AvatarUploadData } from '@/components/Common/AvatarUpload/types'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import { formatDate } from '@/core/utils/time'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useToast } from '@/uiKit/Toast/useToast'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import Divider from '@/uiKit/Divider/Divider.vue'
import DarwinSafeDialog from '@/uiKit/Dialog/DarwinSafeDialog.vue'
import { useUserProfileLoader } from '@/modules/Auth/useUserProfileLoader'

import SettingsPaneV2 from './SettingsPaneV2.vue'
import PasswordChangeModal from './PasswordChangeModal.vue'
import SetupTfaModal from './SetupTfaModal.vue'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'

const userStore = useUserStore()
const teamStore = useTeamStore()
const membershipStore = useMembershipStore()
const authStore = useAuthStore()
const toast = useToast()

const firstName = ref('')
const lastName = ref('')
const confirmDeleteAccountDialoOpen = ref(false)
const changePasswordDialogOpen = ref(false)
const enableTwoFactorAuthentication = ref(false)
const tfaConfigured = ref(false)
const showNotifications = ref(true)
const error = ref<{ firstName?: string; lastName?: string }>({})
const setupTfaDialogOpen = ref(false)
const avatarData = ref<AvatarUploadData | null>(null)
const updatingProfile = ref<boolean>(false)
const deletingAccount = ref<boolean>(false)

const createdAt = computed(() =>
  userStore.currentUser && userStore.currentUser.inserted_at
    ? formatDate(userStore.currentUser.inserted_at, 'MMM D, YYYY')
    : '',
)

const twoFactorEnforced = computed<boolean>(
  () => teamStore.currentTeam?.two_factor_auth_enforced || false,
)

const busy = computed<boolean>(
  () => updatingProfile.value || deletingAccount.value || userStore.loadingUser,
)

const membershipId = computed(() =>
  userStore.currentUser ? membershipStore.memberIdByUserId[userStore.currentUser.id] : undefined,
)

const updateData = (): void => {
  if (!userStore.currentUser) {
    return
  }

  firstName.value = userStore.currentUser.first_name
  lastName.value = userStore.currentUser.last_name
  enableTwoFactorAuthentication.value =
    userStore.currentUser.two_factor_auth_enabled ||
    teamStore.currentTeam?.two_factor_auth_enforced ||
    false
  showNotifications.value = userStore.currentUser.show_notifications
}

watch(() => userStore.currentUser, updateData, { immediate: true })

watch(() => teamStore.currentTeam, updateData, { immediate: true })

const onDeleteAccount = (): void => {
  confirmDeleteAccountDialoOpen.value = true
}

const deleteAccount = async (): Promise<void> => {
  deletingAccount.value = true
  const response = await deleteProfile()
  confirmDeleteAccountDialoOpen.value = false
  deletingAccount.value = false
  if (response.ok) {
    await authStore.logout()
  } else {
    toast.warning({ meta: { title: response.error.message } })
  }
}

const onAvatarChange = (event: AvatarUploadData | null): void => {
  avatarData.value = event
}

const configure2fa = (): void => {
  setupTfaDialogOpen.value = true
}

const validateForm = (): boolean => {
  error.value = {}

  return Object.keys(error.value).length === 0
}

const submit = async (): Promise<void> => {
  if (!validateForm()) {
    return
  }

  updatingProfile.value = true

  const response = await updateProfile({
    first_name: firstName.value,
    last_name: lastName.value,
    two_factor_auth_enabled: enableTwoFactorAuthentication.value,
    show_notifications: showNotifications.value,
    ...(avatarData.value ? { hash: avatarData.value.hash } : {}),
  })

  if (response.ok && avatarData.value) {
    const avatarUploadResponse = await uploadProfileAvatar({
      upload_url: response.data.image?.upload_url as string,
      content: avatarData.value.file,
      type: avatarData.value.type,
    })

    if ('error' in avatarUploadResponse) {
      toast.warning({
        meta: { title: 'There was a problem updating your profile photo. Please try again.' },
      })
      return
    }
  }

  updatingProfile.value = false

  if (!response.ok) {
    if ('isValidationError' in response.error) {
      error.value.firstName = response.error?.first_name as string
      error.value.lastName = response.error?.last_name as string
    } else {
      toast.warning({ meta: { title: response.error.message } })
    }

    return
  }

  userStore.currentUser = response.data
  membershipStore.updateMembershipAvatar(response.data.id, response.data.image)

  avatarData.value = null

  toast.success({ meta: { title: 'Profile successfully updated' } })
}

const onTfaConfirmed = async (): Promise<void> => {
  tfaConfigured.value = true
  await submit()
}

const maybeSubmit = async (): Promise<void> => {
  if (enableTwoFactorAuthentication.value === userStore.currentUser?.two_factor_auth_enabled) {
    await submit()
    return
  }

  if (!enableTwoFactorAuthentication.value) {
    configure2fa()
    return
  }

  if (tfaConfigured.value) {
    return submit()
  }

  configure2fa()
}

onBeforeMount(() => {
  const profileLoader = useUserProfileLoader()
  profileLoader.loadProfile()
})
</script>

<style lang="scss" scoped>
.team__body__section {
  margin-bottom: 16px;
}

.subheader__title {
  @include typographyBoldBody200;
  color: var(--color-text-default);
  margin-bottom: 4px;
}

.subheader__text {
  @include typographyRegularBody200;
  color: var(--color-text-subtlest);
}

.divider {
  margin: 32px 0;
}
.control__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.control__toggle-description {
  flex: 1;
}
.control__toggle-label,
.control__toggle-sub-label {
  @include typographyRegularBody200;
  color: var(--color-text-default);
}

.control__toggle-sub-label {
  color: var(--color-text-subtlest);
}

.team__pair {
  @include typographyRegularBody200;
}

.team__key {
  color: var(--color-text-subtlest);
}

.team__value {
  color: var(--color-text-default);
}

.team__delete {
  margin-top: 30px;
  @include row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &--text {
    flex: 1;
  }
}

.row {
  @include row;
  padding-bottom: 15px;
  gap: 16px;
}
</style>
